//* Import API configurations
import { api } from "../../services/apis/configs/axiosConfigs";

//* Import API URI constant
import { API_URI } from "../../constants/consts";

//* Import PaginationData type
import { type Pagination } from "../../services/stores/types";
import { CSVCollaboratorValidator } from "../../utils/csv/csv-validatorCollaborator";

//* Import types from types
import {
  type Collaborator,
  type CollaboratorImportResult,
  type CollaboratorsDetails,
  type CollaboratorResponse,
} from "./types/types";

// API methods for Collaborator operations
export const collaboratorAPI = {
  // Fetch all Collaborators with optional search and pagination
  getAll: async function (
    search: string = "",
    page: number = 1,
    token: string,
    perPage: number = 10
  ): Promise<CollaboratorResponse> {
    const searchQuery = search ? `&search=${search}` : "";
    const url = `${API_URI}/admin/private/collaborators?page=${page}&perPage=${perPage}${searchQuery}`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;

        const pagination: Pagination = data.pagination
          ? {
              currentPage: data.pagination.currentPage,
              totalPages: data.pagination.totalPages,
              itemsPerPage: data.pagination.itemsPerPage,
              totalItems: data.pagination.totalItems,
            }
          : {
              currentPage: 0,
              totalPages: 0,
              itemsPerPage: 0,
              totalItems: 0,
            };

        return {
          collaborators: data.collaborators || [],
          pagination,
        };
      })
      .catch((error) => {
        console.error("Error fetching Collaborators:", error);
        throw new Error("Failed to fetch Collaborators");
      });
  },

  // Fetch a Collaborator by ID
  getById: async function (
    id: string,
    token: string
  ): Promise<CollaboratorsDetails> {
    return api
      .request({
        url: `${API_URI}/admin/private/collaborators/${id}`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        return response.data.data;
      })
      .catch((error) => {
        console.error("Error fetching collaborator:", error);
        throw new Error("Failed to fetch Collaborator");
      });
  },

  // Create a new collaborators
  create: async function (
    data: { name: string; email: string },
    token: string
  ): Promise<Collaborator> {
    try {
      const response = await api.request({
        url: `${API_URI}/admin/private/collaborators`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data,
      });
      return response.data.data;
    } catch (error) {
      console.error("Error creating admin:", error, data);
      throw error;
    }
  },

  createBatch: async function (
    collaborators: CSVCollaboratorValidator[],
    token: string
  ): Promise<CollaboratorImportResult> {
    return api
      .request({
        url: `${API_URI}/admin/private/collaborators/batch`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: { collaborators },
      })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        console.error("Error processing CSVCollaborator:", error);
        throw error;
      });
  },

  // Update an existing Collaborator
  update: async function (
    id: string,
    data: { name: string; email: string; reset_password?: boolean },
    token: string
  ): Promise<Collaborator> {
    return api
      .request({
        url: `${API_URI}/admin/private/collaborators/${id}`,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error updating collaborator:", error);
        throw new Error("Failed to update Collaborator");
      });
  },

  // Delete an Collaborator by ID
  delete: async function (id: string, token: string): Promise<void> {
    return api
      .request({
        url: `${API_URI}/admin/private/collaborators/${id}`,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error deleting collaborator:", error);
        throw new Error("Failed to delete Collaborator");
      });
  },

  // Restore an Collaborator by ID
  restoreByBatch: async function (id: string, token: string): Promise<void> {
    return api
      .request({
        url: `${API_URI}/admin/private/collaborators/restore/batch/${id}`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error restoring Collaborator:", error);
        throw new Error("Failed to restore Collaborator");
      });
  },

  restoreByEmail: async function (email: string, token: string): Promise<void> {
    return api
      .request({
        url: `${API_URI}/admin/private/collaborators/restore/email/`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: { email },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error restoring Collaborator:", error);
        throw new Error("Failed to restore Collaborator", error);
      });
  },
};
